import { type ComponentProps, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { IsAccessTokenValidQuery } from '../../../generated/hooks';
import { PageTitleAndFavicon } from '../../../utils';
import { questionSummaryKey } from '@arnold/core';
import { useDefinitionMetrics } from '../definition/ReportDefinitionContext';
import { localizedText } from '../definition/types';
import type { AnswerGroup, ReportQuestion } from '../types';
import { ReportHeader } from './ReportHeader';
import { ReportFilters } from './ReportFilters';

interface ReportDetailHeaderProps {
  title?: string;
  description?: string;
  isCompact: boolean;
  scrollerRef: RefObject<HTMLElement>;
  surveyStart: string;
  surveyEnd: string | null | undefined;
  anonymityMode: string;
  organizationName: string;
  rootTeamNames: string[];
  selectedLanguage: string;
  allowedLanguages?: string[];
  onChangeLanguage: (languageCode: string) => void;
  user?: IsAccessTokenValidQuery['isAccessTokenValid']['user'];
  filtersProps: Omit<ComponentProps<typeof ReportFilters>, 'compact'>;
  overviewUrl: string;
  detailQuestion?: ReportQuestion;
  detailMetricId?: string;
  currentGroup: AnswerGroup | null;
}

export const ReportDetailHeader = ({
  title,
  description,
  overviewUrl,
  detailQuestion,
  detailMetricId,
  currentGroup,
  selectedLanguage,
  ...headerProps
}: ReportDetailHeaderProps) => {
  const { t } = useTranslation('newReport');
  const metrics = useDefinitionMetrics();

  const isDetail = !!detailQuestion || !!detailMetricId;
  const detailMetric = metrics.find((metric) => metric.id === detailMetricId);
  const detailSummary = detailQuestion
    ? currentGroup?.questionSummaries.get(questionSummaryKey(detailQuestion.index))
    : undefined;
  const detailTitle =
    detailQuestion?.text || localizedText(detailMetric?.title, selectedLanguage) || t('definition.unavailable');
  const detailDescription = detailMetric ? localizedText(detailMetric.description, selectedLanguage) : undefined;
  const detail = isDetail
    ? {
        overviewUrl,
        subtitle: detailQuestion
          ? `${t(`definition.questionTypes.${detailQuestion.type}`)} · ${t('definition.answers', {
              count: detailSummary?.respondingChannelIds.size ?? 0,
            })}`
          : undefined,
        metric: detailMetric ? currentGroup?.customMetrics[detailMetric.id] : undefined,
        metricFormat: detailMetric?.presentation.format,
      }
    : undefined;

  return (
    <>
      <ReportHeader
        {...headerProps}
        title={isDetail ? detailTitle : title}
        description={isDetail ? detailDescription : description}
        detail={detail}
        selectedLanguage={selectedLanguage}
        questionSummary={detailSummary}
      />
      {title && <PageTitleAndFavicon title={isDetail ? detailTitle : title} />}
    </>
  );
};
